import { takeWhile } from '../utils/ArrayUtils';
import { outerHeight, outerWidth } from '../utils/DomUtils';
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { debounce } from 'debounce';

export type ItemsOrientation = 'horizontal' | 'vertical';

export interface ResizeOptions {
  availableSpace: number;
  items: ResizableItem[];
}
export interface ResizableItem {
  index: number;
  size: number;
  priority: number;
}
export interface ResizeResult {
  restSpace: number;
  itemsSpace: number;
  visibleItems: ResizableItem[];
}

export type GetPriorityCallback = (item: Readonly<{ index: number; element: HTMLElement }>) => number;
export type GetAvailableSpaceCallback = (isResized: boolean, rowNumber?: number) => number;

/**
 * Implementation of Priority+ pattern.
 * Idea: At first to hide elements with less priority
 * https://css-tricks.com/the-priority-navigation-pattern/
 * @param items data associated with rendered elements @see getChildren
 * @param getAvailableSpace available space for items, width if orientation is "horizontal", height if orientation is "vertical"
 * @param getChildren rendered items
 * @param getPriority allows provide priority for each item
 * @param orientation vertical or horizontal
 * @param numberOfRows how many rows to allow for a horizontal layout
 * @param debounceInterval set if you need to slow down number of executions for performance reasons
 * be very careful with this, as it can introduce bugs when the timers conflict with React's internal process timing.
 * (We should probably put in a task to take it back out, and revisit the design of this tool.)
 *
 * @description
 * Method consist of 4 stages:
 * 1. Reset calculated space for items (need if it is updating and not rendered for the first time)
 * 2. Render all items to calculate their sizes
 * 3. Calculate items sizes
 * 4. Render only visible items
 */
export function usePriorityResize<T>(
  items: T[],
  getAvailableSpace: GetAvailableSpaceCallback,
  getChildren: () => HTMLElement[],
  getPriority: GetPriorityCallback,
  orientation: ItemsOrientation = 'horizontal',
  numberOfRows: number = 1,
  debounceInterval = 0
): [T[], () => void] {
  // 2 stage (see description)
  const [visibleItems, _setVisibleItems] = useState<T[]>(items);
  // use a debounce if debounceInterval is provided and > 0
  // debounce was added in haste and is probably the incorrect tool for this, we probably want a throttle, or a requestAnimationFrame
  const setVisibleItems = useMemo(
    () => (debounceInterval > 0 ? debounce(_setVisibleItems, debounceInterval, true) : _setVisibleItems),
    [debounceInterval]
  );
  const resizeInfo = useRef<ResizableItem[] | null>(null);
  const shouldResized = useCallback((availableSpace: number, items: ResizableItem[]) => {
    const requiredSpace = items.reduce((accumulator, currentValue) => accumulator + currentValue.size, 0);
    return availableSpace <= requiredSpace;
  }, []);

  const updateVisibility = useCallback(() => {
    if (
      resizeInfo.current == null ||
      resizeInfo.current.length === 0 ||
      !shouldResized(getAvailableSpace(false, numberOfRows), resizeInfo.current)
    ) {
      // 2 stage (see description)
      setVisibleItems(items);
    } else {
      // 4 stage (see description)
      let newVisibleGroups: T[] = [];
      for (let i = 0; i < numberOfRows; i++) {
        const availableSpace = getAvailableSpace(true, i);
        const resizableItems = [...resizeInfo.current].slice(newVisibleGroups.length, resizeInfo.current.length);
        const visibilityInfo = priorityResize({ items: resizableItems, availableSpace });
        newVisibleGroups.push(...visibilityInfo.visibleItems.map(item => items[item.index] as T));
      }
      setVisibleItems(newVisibleGroups);
    }
  }, [shouldResized, getAvailableSpace, numberOfRows, setVisibleItems, items]);

  useEffect(() => {
    // 1 stage (see description)
    resizeInfo.current = null;
    // 2 stage (see description)
    setVisibleItems(items);
  }, [setVisibleItems, items]);

  useEffect(() => {
    //NOTE: we should recalculate items space only once when all new actions was rendered
    if (resizeInfo.current == null && visibleItems === items) {
      // 3 stage (see description)
      resizeInfo.current = getResizableItems({
        elements: getChildren(),
        prioritize: getPriority,
        orientation,
      });
      updateVisibility();
    }
  }, [getChildren, getPriority, items, orientation, updateVisibility, visibleItems]);
  return [visibleItems, updateVisibility];
}

export function getResizableItems(options: {
  elements: HTMLElement[];
  prioritize?: (item: Readonly<{ index: number; element: HTMLElement }>) => number;
  orientation: ItemsOrientation;
}): ResizableItem[] {
  const items = options.elements.map<ResizableItem>((item, index) => {
    return {
      index,
      size: getSize(item as HTMLElement, options.orientation),
      priority:
        (options.prioritize &&
          options.prioritize({
            index,
            element: item,
          })) ||
        0,
    };
  });
  return items;
}

export function priorityResize({ availableSpace, items }: Readonly<ResizeOptions>): ResizeResult {
  // NOTE: we call "concat" to prevent mutation of original array
  const prioritizedItems = items.concat().sort((item1, item2) => item2.priority - item1.priority);
  let totalSpace = 0;
  const visibleItems = Array.from(
    takeWhile(item => {
      const newAvailableSpace = totalSpace + item.size;
      if (newAvailableSpace > availableSpace) {
        return false;
      }
      totalSpace = newAvailableSpace;
      return true;
    }, prioritizedItems)
  ).sort((item1, item2) => item1.index - item2.index);

  let itemsSpace = 0;
  if (visibleItems.length > 0) {
    itemsSpace = visibleItems.map(item => item.size).reduce((prev, current) => prev + current);
  }

  const restSpace = availableSpace - itemsSpace;
  return {
    restSpace,
    itemsSpace,
    visibleItems,
  };
}

function getSize(element: HTMLElement, orientation: ItemsOrientation) {
  if (orientation === 'horizontal') {
    return outerWidth(element);
  } else if (orientation === 'vertical') {
    return outerHeight(element);
  } else {
    throw new Error(`Unknow orientation value: ${orientation}`);
  }
}
